import { z } from 'zod';
import { CURRENCIES } from '../money/money';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * A customer's promise to pay (PRD → Payments and collections; gitaumoses4/chukua#155), recorded from a collections follow-up.
 * `open` is the absence of a successor and of an outcome; the rest are the system's own record. `kept` and `broken` are decided by
 * the sweep from payment allocations, never typed in. The outcome table's CHECK hard-codes `kept | broken`, so a new one is a migration.
 */
export const PAYMENT_PROMISE_STATUSES = ['open', 'kept', 'broken', 'superseded'] as const;
export type PaymentPromiseStatus = (typeof PAYMENT_PROMISE_STATUSES)[number];

/** What the sweep records; `open` and `superseded` are derived, not stored on an outcome. */
export const PAYMENT_PROMISE_OUTCOMES = ['kept', 'broken'] as const;
export type PaymentPromiseOutcome = (typeof PAYMENT_PROMISE_OUTCOMES)[number];

export const PAYMENT_PROMISE_STATUS_LABELS: Record<PaymentPromiseStatus, string> = {
  open: 'Open',
  kept: 'Kept',
  broken: 'Broken',
  superseded: 'Superseded',
};

export const MAX_PROMISE_INVOICES = 50;

/** A calendar date as the customer said it, not an instant: "by Friday" has no time zone of its own. */
const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date as YYYY-MM-DD');

/** Who recorded it is the signed-in user, never an input. The same body supersedes an open promise. */
export const paymentPromiseInputSchema = z
  .object({
    followUpId: z.string().uuid(),
    promisedDate: calendarDate,
    amountMinor: z.number().int().positive(),
    currency: z.enum(CURRENCIES),
    invoiceIds: z
      .array(z.string().uuid())
      .min(1)
      .max(MAX_PROMISE_INVOICES)
      .refine((ids) => new Set(ids).size === ids.length, 'List each invoice once'),
  })
  .strict();
export type PaymentPromiseInput = z.infer<typeof paymentPromiseInputSchema>;

export interface PaymentPromiseView {
  id: string;
  customerId: string;
  followUpId: string;
  promisedDate: string;
  amountMinor: number;
  currency: (typeof CURRENCIES)[number];
  invoices: { id: string; invoiceNumber: string | null }[];
  status: PaymentPromiseStatus;
  /** The promise that replaced this one; null unless `superseded`. */
  supersededById: string | null;
  /** True on the promised date while the promise is still open, in the company's time zone. */
  isDueToday: boolean;
  /** What the sweep found when it decided a `kept` or `broken` promise; null while open or superseded. */
  outcome: { promisedMinor: number; receivedMinor: number; determinedAt: string } | null;
  recordedBy: { id: string; name: string };
  createdAt: string;
}

/** `GET /customers/:id/payment-promises` — search matches a covered invoice's number. Broken-promise history is `status=broken`. */
export const paymentPromiseListQuerySchema = defineListQuery({
  sortFields: ['promisedDate', 'createdAt'],
  defaultSort: { sort: 'promisedDate', order: 'desc' },
  filters: { status: listFilter(z.enum(PAYMENT_PROMISE_STATUSES)) },
});
export type PaymentPromiseListQuery = z.infer<typeof paymentPromiseListQuerySchema>;

/** `GET /customers/:id/invoices` — the picker's list of issued invoices, the only state a promise may cover. Search matches the invoice number. */
export const customerInvoiceListQuerySchema = defineListQuery({
  sortFields: ['invoiceNumber', 'dueDate', 'issuedAt'],
  defaultSort: { sort: 'dueDate', order: 'desc' },
  filters: { currency: listFilter(z.enum(CURRENCIES)) },
});
export type CustomerInvoiceListQuery = z.infer<typeof customerInvoiceListQuerySchema>;

export interface CustomerInvoiceListItem {
  id: string;
  invoiceNumber: string | null;
  currency: (typeof CURRENCIES)[number];
  grossAmountMinor: number;
  dueDate: string;
  issuedAt: string | null;
}
